import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';

import { DEFAULT_WORKSPACE, WorkspaceId, isWorkspaceId, workspaceForPath } from './workspaces';

/**
 * Which workspace the rail is showing.
 *
 * Shared by the switcher in the header, the menu, and the aurora behind them,
 * which live in different components. Remembered per browser, as a
 * convenience: a vendor who works in Ads comes back to Ads. It is only ever a
 * starting point — the page decides (see `useWorkspaceFollowsRoute`).
 */

const STORAGE_KEY = 'ardealy.bo.workspace';

function readStored(): WorkspaceId {
    try {
        const value = window.localStorage.getItem(STORAGE_KEY);
        return isWorkspaceId(value) ? value : DEFAULT_WORKSPACE;
    } catch {
        // Private windows and blocked storage throw; the default is fine.
        return DEFAULT_WORKSPACE;
    }
}

const GOALS_KEY = 'ardealy.bo.workspace.goals';

/**
 * The workspaces the vendor said they came for (onboarding), most important
 * first. The profile on the server is the truth; this copy lets the switcher
 * draw its order on the first paint, before the profile has loaded.
 */
function readGoals(): WorkspaceId[] {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(GOALS_KEY) || '[]');
        return Array.isArray(parsed) ? parsed.filter(isWorkspaceId) : [];
    } catch {
        return [];
    }
}

interface WorkspaceState {
    workspace: WorkspaceId;
    setWorkspace: (workspace: WorkspaceId) => void;
    goals: WorkspaceId[];
    setGoals: (goals: readonly string[] | undefined) => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
    workspace: readStored(),
    goals: readGoals(),
    setGoals: (goals) => {
        const clean = (goals || []).filter(isWorkspaceId);
        try {
            window.localStorage.setItem(GOALS_KEY, JSON.stringify(clean));
        } catch {
            // Not remembered; the profile brings it back next load.
        }
        set({ goals: clean });
    },
    setWorkspace: (workspace) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, workspace);
        } catch {
            // Not remembered; still switched.
        }
        set({ workspace });
    },
}));

/**
 * Keep the rail on the workspace of the page being shown.
 *
 * Without this, opening /ads/campaigns from the command palette while the rail
 * shows Marketing would leave the current page absent from the menu, which
 * reads as being lost. Shared pages (dashboard, catalogue, billing) belong to
 * no workspace and change nothing.
 */
export function useWorkspaceFollowsRoute() {
    const { pathname } = useLocation();
    const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
    const current = useWorkspaceStore((s) => s.workspace);

    useEffect(() => {
        const owner = workspaceForPath(pathname);
        if (owner && owner !== current) setWorkspace(owner);
        // Only the path drives this; switching by hand must not be undone by it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);
}
